import type { Cable } from '../../types';
import type { BackupPowerIsland, BackupPowerPvTracker, BackupPowerSummary, LogicIssue } from '../../types/deviceModel';
import { clampUnitInterval } from '../../types/deviceModel';
import type { ResolvedDevice } from './resolveModel';
import { analyzeFamilyAtPair, inverterBatteryPorts, inverterMpptPorts, usableBatteryWh } from './polarityGraph';

export function summarizeBackupPower(
  nodes: Map<string, ResolvedDevice>,
  cables: Cable[],
  loadWattsByNode: Map<string, number>,
  issues: LogicIssue[]
): BackupPowerSummary {
  const silent = () => {};
  let loadWatts = 0;
  nodes.forEach((n) => {
    if (n.excluded) return;
    if (n.model.isSolar || n.model.isBattery || n.model.isInverter || n.model.isPsu || n.model.isMainsOutlet) return;
    loadWatts += loadWattsByNode.get(n.nodeId) || 0;
  });
  loadWatts = Math.round(loadWatts * 10) / 10;

  const islands: BackupPowerIsland[] = [];

  nodes.forEach((node) => {
    if (node.excluded || !node.model.isInverter) return;
    const bat = inverterBatteryPorts(node);
    let batteryVolts = 0;
    let batteryAh = 0;
    if (bat) {
      const reduced = analyzeFamilyAtPair(nodes, cables, 'bat', bat.plusKey, bat.minusKey, silent);
      if (reduced.branch) {
        batteryVolts = reduced.branch.volts || reduced.branch.voc;
        batteryAh = reduced.branch.ah;
      }
    }
    let solarStcWatts = 0;
    let pvUsedWatts = 0;
    let pvMaxWatts = 0;
    const pvInputs = node.model.powerInputs.filter((i) => i.kind === 'pv' && i.enabled !== false);
    const pvTrackers: BackupPowerPvTracker[] = [];
    pvInputs.forEach((input, index) => {
      pvMaxWatts += input.maxWatts || 0;
      const pair = inverterMpptPorts(node, input.mpptId || input.id);
      let arrayVolts = 0;
      if (pair) {
        const reduced = analyzeFamilyAtPair(nodes, cables, 'pv', pair.plusKey, pair.minusKey, silent);
        if (reduced.branch) {
          solarStcWatts += reduced.branch.pmax;
          pvUsedWatts += reduced.branch.pmax;
          arrayVolts = reduced.branch.vmp || 0;
        }
      }
      const label = pvInputs.length > 1 ? `MPPT ${index + 1}` : 'Array';
      pvTrackers.push({
        id: input.mpptId || input.id,
        label,
        arrayVolts: Math.round(arrayVolts * 10) / 10,
        mpptMinVolts: input.mpptVoltageMin ?? null,
        mpptMaxVolts: input.mpptVoltageMax ?? null,
      });
    });
    const efficiency = clampUnitInterval(node.model.inverterEfficiency, 0.97);
    const acOut = (node.model.powerOutputs || []).find((o) => o.kind === 'ac' && o.enabled !== false);
    const inverterAcWatts = acOut?.maxWatts || 0;
    const usableWh = usableBatteryWh(node.model, batteryVolts, batteryAh);
    const dcDraw = loadWatts > 0 ? loadWatts / efficiency : 0;
    const overnightHours = usableWh > 0 && dcDraw > 0 ? Math.round((usableWh / dcDraw) * 10) / 10 : null;
    islands.push({
      inverterId: node.nodeId,
      inverterName: node.name,
      batteryVolts,
      batteryAh,
      usableWh: Math.round(usableWh * 10) / 10,
      solarStcWatts: Math.round(solarStcWatts * 10) / 10,
      pvUsedWatts: Math.round(pvUsedWatts * 10) / 10,
      pvMaxWatts,
      pvTrackers,
      loadWatts,
      inverterAcWatts,
      efficiency,
      overnightHours,
    });
  });

  const volts = islands.map((i) => i.batteryVolts).filter((v) => v > 0);
  const sameBus = volts.length <= 1 || Math.max(...volts) - Math.min(...volts) < 1;
  const usableWh = sameBus ? islands.reduce((s, i) => s + i.usableWh, 0) : 0;
  const overnightHours =
    islands.length === 1
      ? islands[0].overnightHours
      : sameBus && usableWh > 0 && loadWatts > 0 && islands[0]
        ? Math.round((usableWh / (loadWatts / islands[0].efficiency)) * 10) / 10
        : null;

  void issues;
  return {
    islands,
    solarStcWatts: islands.reduce((s, i) => s + i.solarStcWatts, 0),
    usableWh,
    loadWatts,
    overnightHours,
  };
}
